type CacheEntry<T> = {
  data: T;
  timestamp: number;
};

/**
 * Simple browser cache utility that stores data in localStorage
 * with expiration support
 */
export const browserCache = {
  /**
   * Get a cached item if it exists and hasn't expired
   *
   * @param key Cache key
   * @param maxAgeMinutes Maximum age in minutes before item expires
   * @returns The cached data or null if not found or expired
   */
  get<T>(key: string, maxAgeMinutes: number = 60): T | null {
    try {
      const item = localStorage.getItem(`cache_${key}`);
      if (!item) return null;

      const cacheEntry: CacheEntry<T> = JSON.parse(item);
      const now = Date.now();

      // Check if the item has expired
      if (now - cacheEntry.timestamp > maxAgeMinutes * 60 * 1000) {
        localStorage.removeItem(`cache_${key}`);
        return null;
      }

      return cacheEntry.data;
    } catch (error) {
      console.error("Cache error:", error);
      return null;
    }
  },

  /**
   * Save an item to cache
   *
   * @param key Cache key
   * @param data Data to store
   */
  set<T>(key: string, data: T): void {
    try {
      const cacheEntry: CacheEntry<T> = {
        data,
        timestamp: Date.now(),
      };

      localStorage.setItem(`cache_${key}`, JSON.stringify(cacheEntry));
    } catch (error) {
      console.error("Cache set error:", error);
    }
  },
};
